import { ErrorActorEvent, assertEvent } from 'xstate';
import { ActionEventParams, ActorErrorParams } from '../../../../lib/types';
import {
  CreatePaymentMethodActorDone,
  EvaluateErrorResponseActorDone,
  FetchPaymentMethodActorDone,
  FetchPaymentMethodsActorDone,
  SendAssignSelectedPaymentMethod,
  SendAssignSelectedPaymentMethodParams,
} from './types/events';
import {
  CreatePaymentMethodResult,
  EvaluateErrorResponseResult,
  FetchPaymentMethodResult,
  FetchPaymentMethodsResult,
} from './types/actors';
import { PaymentsContext } from './paymentsMachine';

/**
 * Gets FetchPaymentMethodResult from FetchPaymentMethodActorDone event
 * @param ActionEventParams<PaymentsContext, FetchPaymentMethodActorDone>
 * @returns FetchPaymentMethodResult
 */
export const getFetchPaymentMethodParams = ({
  event,
}: ActionEventParams<
  PaymentsContext,
  FetchPaymentMethodActorDone
>): FetchPaymentMethodResult => {
  return event.output;
};

/**
 * Gets FetchPaymentMethodsResult from FetchPaymentMethodsActorDone event
 * @param ActionEventParams<PaymentsContext, FetchPaymentMethodsActorDone>
 * @returns FetchPaymentMethodsResult
 */
export const getFetchPaymentMethodsParams = ({
  event,
}: ActionEventParams<
  PaymentsContext,
  FetchPaymentMethodsActorDone
>): FetchPaymentMethodsResult => {
  return event.output;
};

/**
 * Gets CreatePaymentMethodResult from CreatePaymentMethodActorDone event
 * @param ActionEventParams<PaymentsContext, CreatePaymentMethodActorDone>
 * @returns CreatePaymentMethodResult
 */
export const getCreatePaymentMethodParams = ({
  event,
}: ActionEventParams<
  PaymentsContext,
  CreatePaymentMethodActorDone
>): CreatePaymentMethodResult => {
  return event.output;
};

/**
 * Gets payment error evaluation entry params from upstream actor done events.
 * This is intended for `evaluatingErrorResponse` entry actions.
 * @param ActionEventParams<PaymentsContext,
 * FetchPaymentMethodActorDone | FetchPaymentMethodsActorDone |
 * CreatePaymentMethodActorDone>
 * @returns FetchPaymentMethodResult | FetchPaymentMethodsResult | CreatePaymentMethodResult
 */
export const getEvaluateErrorResponseEntryParams = ({
  event,
}: ActionEventParams<
  PaymentsContext,
  | FetchPaymentMethodActorDone
  | FetchPaymentMethodsActorDone
  | CreatePaymentMethodActorDone
>):
  | FetchPaymentMethodResult
  | FetchPaymentMethodsResult
  | CreatePaymentMethodResult => {
  return event.output;
};

/**
 * Get SendAssignSelectedPaymentMethodParams from SendAssignSelectedPaymentMethod event
 * @param ActionEventParams<PaymentsContext, SendAssignSelectedPaymentMethod>
 * @returns SendAssignSelectedPaymentMethodParams
 */
export const getAssignSelectedPaymentMethodParams = ({
  event,
}: ActionEventParams<
  PaymentsContext,
  SendAssignSelectedPaymentMethod
>): SendAssignSelectedPaymentMethodParams => {
  assertEvent(event, 'SEND_ASSIGN_SELECTED_PAYMENT_METHOD');

  return event.params;
};

/**
 * Gets EvaluateErrorResponseResult from EvaluateErrorResponseActorDone event
 * @param ActionEventParams<PaymentsContext, EvaluateErrorResponseActorDone>
 * @returns EvaluateErrorResponseResult
 */
export const evaluateErrorResponseParams = ({
  event,
}: ActionEventParams<
  PaymentsContext,
  EvaluateErrorResponseActorDone
>): EvaluateErrorResponseResult => {
  return event.output;
};

/**
 * Gets ActorErrorParams from error event
 * @param ActionEventParams<PaymentsContext, ErrorActorEvent>
 * @returns ActorErrorParams
 */
export const getServiceErrorParams = ({
  event,
}: ActionEventParams<PaymentsContext, ErrorActorEvent>): ActorErrorParams => {
  const errorOutput = event.error as ActorErrorParams;

  return {
    type: event.type,
    error: errorOutput.error,
    buyerID: errorOutput.buyerID,
    reject: errorOutput.reject,
  };
};
